import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {ActionResult, EventItem, EventStatus, LeagueFilter, Lookups, PagedFilter} from "@model";
import {Observable, map, BehaviorSubject, tap} from "rxjs";
import {League} from "@model";
import {LoaderService} from "./loader.service";
import dayjs from "dayjs";
import {EnvironmentService} from "./environment.service";

@Injectable({ providedIn:'root' })
export class LeagueService {

  private static _currentLeague: BehaviorSubject<League | undefined> = new BehaviorSubject<League | undefined>(undefined);

  get currentLeague(): BehaviorSubject<League | undefined> {
    return LeagueService._currentLeague;
  }

  constructor(private http: HttpClient, private loaderService:LoaderService, private environmentService:EnvironmentService) {
  }

  setCurrentLeague(league: League | undefined): void {
    this.currentLeague.next(league);
  }

  getFilteredLeagueStatuses(): Observable<Lookups[] | []> {
    return this.http.get<ActionResult<Lookups[]>>(this.environmentService.getApiUrl() + "/api/public/lookups/leaguestatusesfiltered2").pipe(
      map((value: ActionResult<Lookups[]>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  findLeagues(): Observable<League[]> {
    return this.http.get<ActionResult<League[]>>(this.environmentService.getApiUrl() + "/api/leagues/fetchall").pipe(
      map((actionResult: ActionResult<League[]>) => {
        if (actionResult.resultCode !== -1 && actionResult.result) {
          return actionResult.result;
        } else {
          return [];
        }
      }));
  }

  findPublicLeagues(filter: PagedFilter<LeagueFilter>): Observable<League[]> {
    return this.http.post<ActionResult<League[]>>(this.environmentService.getApiUrl() + "/api/public/leagues", filter).pipe(
      map((actionResult: ActionResult<League[]>) => {
        if (actionResult.resultCode !== -1 && actionResult.result) {
          return actionResult.result;
        } else {
          return [];
        }
      }));
  }

  fetchLeague(leagueId: number): Observable<League | undefined> {
    return this.http.post<ActionResult<League>>(this.environmentService.getApiUrl() + "/api/leagues/fetchbyid", leagueId).pipe(
      map((actionResult: ActionResult<League>): League | undefined => {
        if (actionResult.resultCode !== -1 && actionResult.result) {
          this.currentLeague.next(actionResult.result);
          return actionResult.result;
        } else {
          this.currentLeague.next(undefined);
          return undefined;
        }
      }));
  }

  fetchPublicLeague(leagueId: number): Observable<League | undefined> {
    return this.http.post<ActionResult<League>>(this.environmentService.getApiUrl() + "/api/public/leagues/fetchbyid", leagueId).pipe(
      map((actionResult: ActionResult<League>): League | undefined => {
        if (actionResult.resultCode !== -1 && actionResult.result) {
          this.sortEvents(actionResult.result);
          this.currentLeague.next(actionResult.result);
          return actionResult.result;
        } else {
          this.currentLeague.next(undefined);
          return undefined;
        }
      }));
  }

  /**
   * Sort events, first by active/finished and then by start date
   * @param league
   */
  private sortEvents(league:League) {
    if ((league?.events ?? 0 > 0)) {
      let finishedEvents: EventItem[] = league!.events!.filter((eventItem:EventItem)=>eventItem.status === EventStatus.FINISHED);
      let activeEvents: EventItem[] = league!.events!.filter((eventItem:EventItem)=>eventItem.status === EventStatus.ACTIVE);
      finishedEvents.sort((eventItem1:EventItem, eventItem2:EventItem)=>{
        return dayjs(eventItem1.start!).toDate().getTime() - dayjs(eventItem2!.start!).toDate().getTime();
      });
      activeEvents.sort((eventItem1:EventItem, eventItem2:EventItem)=>{
        return dayjs(eventItem1.start!).toDate().getTime() - dayjs(eventItem2!.start!).toDate().getTime();
      });
      league.events = [...activeEvents, ...finishedEvents];
    }
  }

  updateLeague(league: League, onSuccess: (league: League) => void, onError: () => void): void {
    this.http.post<ActionResult<boolean|number>>(this.environmentService.getApiUrl() + (league.id ? "/api/leagues/update" : "/api/leagues/add"), league)
      .subscribe((value: ActionResult<boolean|number>):void => {
        if (value && value.result) {
          if (typeof value.result === "boolean") {
            this.setCurrentLeague(league);
          } else {
            league.id = value.result;
            this.setCurrentLeague(league);
          }
          onSuccess(league);
        } else {
          onError();
        }
      });
  }

  deleteLeague(league: League, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/leagues/delete", league.id)
      .subscribe((actionResult: ActionResult<boolean>): void => {
        if (actionResult.success) {
          onSuccess();
        } else {
          onError();
        }
      });
  }

  availableEventsForLeague(leagueId?: number): Observable<EventItem[]> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<EventItem[]>>(this.environmentService.getApiUrl() + `/api/leagues/availableevents/${leagueId}` ,undefined).pipe(tap(() => {
      this.loaderService.dismissLoading();
    }), map((value: ActionResult<EventItem[]>) => {
      if (value && value.result && value.result.length != 0) {
        return value.result;
      } else {
        return [];
      }
    }));
  }

  addEventsToLeague(league: League, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/leagues/addleagueevents", league)
      .subscribe((actionResult: ActionResult<boolean>): void => {
        if (actionResult.success) {
          onSuccess();
        } else {
          onError();
        }
      });
  }

  removeEventFromLeague(eventId: number, leagueId: number, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() +  `/api/leagues/removeevent/eventId/${eventId}/leagueId/${leagueId}`,undefined)
      .subscribe((actionResult: ActionResult<boolean>): void => {
        if (actionResult.success) {
          onSuccess();
        } else {
          onError();
        }
      });
  }
}
